import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { AuthUser, LoginResponse, TokenPair } from '../types';
import {
  loginWithPassword,
  requestMagicLink,
  verifyMagicLink,
} from '../api/auth';
import { resetCsrfToken } from '../api/csrf';
import { clearAllSessionNotes } from '../lib/sessionNotes';

const configured =
  typeof import.meta.env.VITE_API_BASE_URL === 'string'
    ? import.meta.env.VITE_API_BASE_URL.trim()
    : '';
const apiBaseUrl = configured.length > 0 ? configured : '/api';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  user: AuthUser | null;

  /** État du flux de connexion. */
  loginError: string | null;
  linkRequested: boolean;
  requesting: boolean;
  verifying: boolean;
  loggingIn: boolean;

  login: (credentials: { email: string; password: string }) => Promise<boolean>;
  requestLink: (email: string) => Promise<void>;
  verify: (token: string) => Promise<boolean>;
  setSession: (session: LoginResponse) => void;
  setUser: (user: AuthUser) => void;
  logout: () => Promise<void>;
  setTokens: (tokens: TokenPair) => void;
  clearAuth: () => void;
  resetFlow: () => void;
}

/** Vérifie qu'un compte authentifié possède bien le rôle moniteur. */
export function isInstructor(user: AuthUser | null): boolean {
  return user?.role === 'instructor';
}

function extractMessage(error: unknown, fallback: string): string {
  if (
    axios.isAxiosError(error) &&
    typeof error.response?.data === 'object' &&
    error.response?.data !== null
  ) {
    const msg = (error.response.data as Record<string, unknown>).message;
    if (typeof msg === 'string' && msg) return msg;
  }
  return fallback;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      user: null,
      loginError: null,
      linkRequested: false,
      requesting: false,
      verifying: false,
      loggingIn: false,

      setSession: (session) =>
        set({
          token: session.tokens.accessToken,
          refreshToken: session.tokens.refreshToken,
          tokenExpiresAt: Date.now() + session.tokens.expiresIn * 1000,
          user: session.user,
          loginError: null,
          linkRequested: false,
        }),

      setUser: (user) => set({ user }),

      login: async (credentials) => {
        set({ loggingIn: true, loginError: null });
        try {
          const data = await loginWithPassword(
            credentials.email,
            credentials.password,
          );
          if (data.user.role !== 'instructor') {
            set({
              loginError:
                "Ce compte n'est pas un compte moniteur. Utilisez l'application principale.",
            });
            return false;
          }
          get().setSession(data);
          return true;
        } catch (error) {
          set({
            loginError: extractMessage(
              error,
              'Email ou mot de passe incorrect.',
            ),
          });
          return false;
        } finally {
          set({ loggingIn: false });
        }
      },

      requestLink: async (email) => {
        set({ requesting: true, loginError: null, linkRequested: false });
        try {
          await requestMagicLink(email);
          set({ linkRequested: true });
        } catch (error) {
          set({
            loginError: extractMessage(
              error,
              "Impossible d'envoyer le lien pour le moment. Réessayez plus tard.",
            ),
          });
        } finally {
          set({ requesting: false });
        }
      },

      verify: async (token) => {
        set({ verifying: true, loginError: null });
        try {
          const data = await verifyMagicLink(token);
          get().setSession(data);
          return true;
        } catch (error) {
          set({
            loginError: extractMessage(
              error,
              'Ce lien de connexion est invalide ou a expiré. Demandez-en un nouveau.',
            ),
          });
          return false;
        } finally {
          set({ verifying: false });
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        try {
          if (refreshToken) {
            const client = axios.create({
              baseURL: apiBaseUrl,
              withCredentials: true,
            });
            await client.post(
              '/v1/auth/logout',
              { refreshToken },
              { headers: { Authorization: `Bearer ${get().token}` } },
            );
          }
        } catch {
          // best-effort
        } finally {
          // Notes de la séance gardées sur l'appareil (J31) : elles partent avec le moniteur.
          clearAllSessionNotes(get().user?.id);
          get().clearAuth();
        }
      },

      setTokens: (tokens) =>
        set({
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          tokenExpiresAt: Date.now() + tokens.expiresIn * 1000,
        }),

      clearAuth: () => {
        resetCsrfToken();
        set({
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          user: null,
          linkRequested: false,
          loginError: null,
        });
      },

      resetFlow: () =>
        set({ loginError: null, linkRequested: false }),
    }),
    {
      name: 'pmd-moniteur-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        user: state.user,
      }),
    },
  ),
);
